import React, { useEffect, useState } from 'react';
import { GameState } from '../../types';
import { X, Lightbulb } from 'lucide-react';

interface OnboardingPopupsProps {
    gameState: GameState;
    currentTab: string; // 'leftPanel' or 'investigation'
    leftPanelTab: string; // 'map', 'notes', 'suspects', 'clues', 'history'
    onCompleteTutorial: (tutorialId: string) => void;
}

interface TutorialStep {
    id: string;
    condition: (props: OnboardingPopupsProps) => boolean;
    title: string;
    text: string;
    position: 'center' | 'top-right' | 'bottom';
}

const steps: TutorialStep[] = [
    {
        id: 'tut_hq_start',
        condition: (p) => p.gameState.status === 'INVESTIGATING' && p.gameState.activeCases.find(c => c.id === p.gameState.selectedCaseId)?.currentLocationId === 'HQ',
        title: 'Bem-vindo ao QG',
        text: 'Este é o seu escritório. Leia o Dossiê do Caso na mesa. Aqui você também pode usar a Central Telefônica para ligar para suspeitos que já encontrou.',
        position: 'center'
    },
    {
        id: 'tut_map',
        condition: (p) => p.gameState.status === 'INVESTIGATING' && p.leftPanelTab === 'map',
        title: 'Deslocamento',
        text: 'No Mapa você escolhe seu próximo destino. Viagens consomem HORAS de jogo. Seja estratégico para não perder o prazo do caso.',
        position: 'center'
    },
    {
        id: 'tut_investigate',
        condition: (p) => p.gameState.status === 'INVESTIGATING' && p.gameState.activeCases.find(c => c.id === p.gameState.selectedCaseId)?.currentLocationId !== 'HQ' && p.currentTab === 'investigation',
        title: 'Vasculhe as Cenas',
        text: 'Ao chegar a um local, VASCULHE a área. Isso consome tempo, mas revela pistas e pessoas presentes no local. Depois, interrogue quem estiver por perto.',
        position: 'center'
    },
    {
        id: 'tut_clues',
        condition: (p) => p.gameState.status === 'INVESTIGATING' && p.leftPanelTab === 'clues',
        title: 'Evidências e Itens',
        text: 'Aba de Pistas! Aqui ficam registrados todos os fatos descobertos. Itens físicos (como armas ou celulares) aparecerão aqui e você pode INSPECIIONÁ-LOS para descobrir novos locais.',
        position: 'center'
    }
];

export const OnboardingPopups: React.FC<OnboardingPopupsProps> = (props) => {
    const { gameState, onCompleteTutorial } = props;
    const [currentPopup, setCurrentPopup] = useState<TutorialStep | null>(null);

    useEffect(() => {
        if (gameState.selectedCaseId !== 'tutorial-case-001') {
            setCurrentPopup(null);
            return;
        }

        // Find the first matching step that hasn't been viewed
        const activeStep = steps.find(
            step => !(gameState.viewedTutorials || []).includes(step.id) && step.condition(props)
        );

        if (activeStep) {
            setCurrentPopup(activeStep);
        } else {
            setCurrentPopup(null);
        }
    }, [gameState, props.currentTab, props.leftPanelTab]);

    if (!currentPopup || gameState.selectedCaseId !== 'tutorial-case-001') return null;

    const handleDismiss = () => {
        onCompleteTutorial(currentPopup.id);
        setCurrentPopup(null);
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-amber-950/90 border-2 border-amber-500 max-w-sm w-full rounded-xl shadow-[0_0_30px_rgba(245,158,11,0.3)] relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2">
                    <button onClick={handleDismiss} className="text-amber-500 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="p-6 text-center">
                    <Lightbulb className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse drop-shadow-[0_0_10px_rgba(245,158,11,0.8)]" />
                    <h3 className="text-xl font-bold font-mono text-white mb-2 uppercase tracking-widest">{currentPopup.title}</h3>
                    <p className="text-amber-100/80 text-sm leading-relaxed mb-6">
                        {currentPopup.text}
                    </p>
                    
                    <button 
                        onClick={handleDismiss}
                        className="bg-amber-600 hover:bg-amber-500 text-black font-bold uppercase tracking-wide py-2 px-8 rounded-lg transition-transform transform hover:scale-105"
                    >
                        ENTENDIDO
                    </button>
                </div>
            </div>
        </div>
    );
};
